/**
 * Reminder measurement view-model (F10 / MEAS-002/003/004; D9/D10).
 * Turns one `GET /reminders/stats-summary` summary (RRS docs/reminders-stats-summary.md) into what
 * the portal shows. Rules:
 *  - a value the server sent (including 0) is a number; a field an older backend leaves out is
 *    `null` ("not reported") so the UI shows "—", never a fake 0;
 *  - iOS "observed" (a lower bound) and "estimated" are two separate numbers. Nothing here adds
 *    them together, and there is no combined "delivered" total.
 */

const REASON_LABELS = {
  // schedule_failed
  date_missing: 'Date missing in the event',
  no_timezone: 'Date had no time zone',
  time_passed: 'Time already passed',
  quiet_hours_past_anchor: 'Quiet hours ran past the date',
  daily_limit: 'Daily limit reached',
  notifications_off: 'Notifications turned off',
  os_budget: 'Phone notification limit (iOS 64)',
  location_unavailable: 'Location unavailable',
  before_cutoff: 'Before the first-open cutoff',
  invalid_payload: 'Invalid rule on the phone',
  unsupported_type: 'App version does not support this type',
  unknown: 'Unknown reason',
  // cancelled
  replaced: 'Replaced by a newer event',
  cancel_event: 'Cancel event arrived',
  rule_removed: 'Reminder paused or removed',
  version_superseded: 'Reminder was edited',
  end_date: 'Reached its end date',
  logged_out: 'User logged out',
};

const humanise = (k) => {
  const s = String(k || '').replace(/_/g, ' ').trim();
  return s ? s[0].toUpperCase() + s.slice(1) : 'Unknown reason';
};
export const reasonLabel = (reason) => REASON_LABELS[reason] || humanise(reason);

// A count the server reported: finite and ≥ 0. Anything else → null (not reported / unusable).
const count = (v) => {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

export const pctOf = (part, whole) => {
  if (part == null || whole == null || !(whole > 0)) return null;
  return Math.round((part / whole) * 100);
};

const reasonBlock = (b) => {
  if (!b || typeof b !== 'object') return null;
  const reasons = Object.entries(b.by_reason && typeof b.by_reason === 'object' ? b.by_reason : {})
    .map(([reason, v]) => ({ reason, label: reasonLabel(reason), count: count(v) }))
    .filter((r) => r.count != null && r.count > 0)
    .sort((a, z) => z.count - a.count || a.reason.localeCompare(z.reason));
  const total = count(b.total);
  return { total: total != null ? total : reasons.reduce((s, r) => s + r.count, 0), reasons };
};

const conversionBlock = (c) => {
  if (!c || typeof c !== 'object' || !c.goal_event) return null;
  const tappers = count(c.unique_tappers);
  const conversions = count(c.conversions);
  const rate = count(c.conversion_rate);
  return {
    goalEvent: String(c.goal_event),
    attributionSeconds: count(c.attribution_seconds),
    tappers,
    conversions,
    rate: rate != null ? rate : pctOf(conversions, tappers),
  };
};

export function normalizeReminderStats(s) {
  const src = s && typeof s === 'object' ? s : {};
  const scheduled = count(src.scheduled);
  const tapped = count(src.tapped);
  const tapRateRaw = count(src.tap_rate);
  const d = src.delivered;
  const delivered = d && typeof d === 'object'
    ? { android: count(d.android), iosObserved: count(d.ios_observed), iosEstimated: count(d.ios_estimated) }
    : null;
  const out = {
    scheduled,
    tapped,
    tapRate: tapRateRaw != null ? tapRateRaw : (scheduled === 0 && tapped != null ? 0 : pctOf(tapped, scheduled)),
    delivered,
    dismissed: count(src.dismissed),
    scheduleFailed: reasonBlock(src.schedule_failed),
    cancelled: reasonBlock(src.cancelled),
    conversion: conversionBlock(src.conversion),
  };
  // Older backend (pre-D9): the post-tap blocks are absent entirely.
  out.legacy = !('delivered' in src) || !('schedule_failed' in src) || !('cancelled' in src) || !('dismissed' in src);
  return out;
}

export const formatCount = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US'));
export const formatPct = (n) => (n == null ? '—' : `${n}%`);

export function formatWindow(seconds) {
  if (!(seconds > 0)) return '—';
  const unit = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
  if (seconds % 86400 === 0) return unit(seconds / 86400, 'day');
  if (seconds % 3600 === 0) return unit(seconds / 3600, 'hour');
  if (seconds % 60 === 0) return unit(seconds / 60, 'minute');
  return unit(seconds, 'second');
}

export function iosDeliveredText(delivered) {
  if (!delivered) return '—';
  return `${formatCount(delivered.iosObserved)} observed · ${formatCount(delivered.iosEstimated)} estimated`;
}

// Compact form for the list column.
export function deliveredShort(delivered) {
  if (!delivered) return '—';
  return `Android ${formatCount(delivered.android)} · iOS ${formatCount(delivered.iosObserved)} observed / ${formatCount(delivered.iosEstimated)} est.`;
}

// Scheduled → tapped (→ converted when the reminder has a goal). Each rate uses its own denominator:
// tap rate is of scheduled; conversion rate is of unique tappers (the In-App window funnel, D10).
export function funnelSteps(stats) {
  const steps = [
    { key: 'scheduled', label: 'Scheduled', value: stats.scheduled, rate: null, rateOf: null },
    { key: 'tapped', label: 'Tapped', value: stats.tapped, rate: stats.tapRate, rateOf: 'of scheduled' },
  ];
  if (stats.conversion) {
    steps.push({ key: 'converted', label: `Converted (${stats.conversion.goalEvent})`, value: stats.conversion.conversions, rate: stats.conversion.rate, rateOf: 'of tappers' });
  }
  return steps;
}
